import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

import { vncWebSocketUrl as tunnelUrl } from "@/lib/vncTunnel";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Browser farm — flotte de navigateurs serveur.
 *
 * Un pod = un compte LinkedIn = un Chrome réel avec l'extension RecrutAuto
 * chargée, derrière le proxy de ce compte. L'ordinal du pod (`browserfarm-2`)
 * ne dit RIEN du compte qu'il tient : c'est le bail en base qui décide, et
 * l'API refuse plutôt que de deviner.
 */

export type FleetState = "unassigned" | "stale" | "running" | "idle" | "bootstrapping" | "degraded" | string;

export interface FleetRow {
  subscription_id: number;
  user_id?: string | null;
  linkedin_account_urn?: string | null;
  pod_hostname?: string | null;
  /** Extension worker_id registered on the lease: back/ serves the account's jobs to it only. */
  worker_id?: string | null;
  state: FleetState;
  /** Why `degraded`: the pod's own health detail, or `no_job_poll`. */
  degraded_reason?: string | null;
  last_job_poll_at?: string | null;
  heartbeat_at?: string | null;
  claimed_at?: string | null;
  /** Host:port seulement — les identifiants ne sortent jamais du serveur. */
  proxy_endpoint?: string | null;
  proxy_country?: string | null;
  challenged_at?: string | null;
  challenge_kind?: string | null;
}

export interface VncTicket {
  token: string;
  pod_hostname: string;
  ws_path: string;
  /** Offered as a WebSocket subprotocol; the token never goes in the URL. */
  subprotocol: string;
  expires_in: number;
}

export interface PodActionResult {
  pod_hostname: string;
  ok: boolean;
  detail?: string | null;
}

async function unwrap<T>(res: Response | undefined): Promise<T> {
  if (!res?.ok) {
    let detail = `Erreur ${res?.status ?? "?"}`;
    try {
      const body = await res?.json();
      if (body?.detail) detail = `${detail} : ${body.detail}`;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function listFleet(navigate: NavigateFunction): Promise<FleetRow[]> {
  const res = await apiFetch(`${API_URL}/browserfarm/fleet`, { method: "GET" }, navigate);
  return unwrap<FleetRow[]>(res);
}

export async function requestVncTicket(
  subscriptionId: number,
  navigate: NavigateFunction,
): Promise<VncTicket> {
  const res = await apiFetch(
    `${API_URL}/browserfarm/${subscriptionId}/vnc-ticket`,
    { method: "POST" },
    navigate,
  );
  return unwrap<VncTicket>(res);
}

async function podAction(
  subscriptionId: number,
  action: "restart" | "bootstrap/start" | "bootstrap/finish",
  navigate: NavigateFunction,
): Promise<PodActionResult> {
  const res = await apiFetch(
    `${API_URL}/browserfarm/${subscriptionId}/${action}`,
    { method: "POST" },
    navigate,
  );
  return unwrap<PodActionResult>(res);
}

export const restartBrowser = (id: number, nav: NavigateFunction) =>
  podAction(id, "restart", nav);
export const startBootstrap = (id: number, nav: NavigateFunction) =>
  podAction(id, "bootstrap/start", nav);
export const finishBootstrap = (id: number, nav: NavigateFunction) =>
  podAction(id, "bootstrap/finish", nav);

/** WebSocket URL of the noVNC tunnel (no ticket in it, cf. lib/vncTunnel). */
export function vncWebSocketUrl(): string {
  return tunnelUrl(API_URL, window.location.origin);
}
